import { useEffect, useRef } from 'react';
import { assets } from '../assets';
import { Button } from '../components/Button';
import { PersistentVideo } from '../components/PersistentVideo';
import { Screen, Title } from '../components/Screen';
import { VideoPlaceholder } from '../components/VideoPlaceholder';
import { copy } from '../copy';
import type { AppConfig } from '../config';
import { noteEvent } from '../lib/diagnostics';

/**
 * Shared: web step 5, kiosk step 3. The siblings squawking, plus the line that
 * tells the guest what to do.
 *
 * The clip is unmuted here: this is the one place the guest needs to hear
 * something, and on web the tap that got them off the title screen is the
 * gesture that unlocks audio playback.
 *
 * The two modes diverge in shape, not just scale:
 *
 *   web   — heading, clip, and an "I am ready" button. A guest can move on
 *           before it ends; nobody should be held hostage by a video.
 *   kiosk — the clip full-bleed and nothing else. No heading, no button: there
 *           is no one to read chrome to, the guest is standing a metre back, and
 *           the clip ending is what moves them to the camera.
 */

/**
 * Kiosk watchdog. Removing the button means the clip ending is the only way
 * forward, so a clip that loads and then stalls would strand a guest in front of
 * an unattended screen. A missing file is already handled by VideoPlaceholder,
 * which fires onEnded after 3s; this covers the stall that never errors.
 *
 * Sized against the clip, which is why it moved: the cut delivered 2026-09-17
 * runs 3.1s, down from 19.7s, and the old 25s ceiling would have held a guest
 * in front of a frozen frame for eight times the clip's own length before
 * giving up. 10s keeps a wide margin for a slow first play on the panel while
 * staying clear of VideoPlaceholder's own 3s fallback. A longer recut needs
 * this raised to match — see public/assets/video/README.md.
 */
const KIOSK_MAX_WAIT_MS = 10_000;

export function InstructionScreen({
  config,
  onDone,
}: {
  config: AppConfig;
  onDone: () => void;
}) {
  const kiosk = config.mode === 'kiosk';

  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!kiosk) return;
    const t = window.setTimeout(() => {
      noteEvent('instruction watchdog — advancing without an ended event');
      doneRef.current();
    }, KIOSK_MAX_WAIT_MS);
    return () => clearTimeout(t);
  }, [kiosk]);

  if (kiosk) {
    // Preloaded at boot and moved in here, so the first frame is on screen the
    // moment this mounts — see PersistentVideo. Creating the element here cost
    // ~0.7s of grey placeholder per guest on the panel.
    return (
      <div data-testid="instruction" className="relative h-full min-h-[var(--app-h)] w-full overflow-hidden bg-ink">
        <PersistentVideo
          id="instruction"
          src={assets.instruction}
          label="Instruction clip"
          muted={false}
          onEnded={onDone}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    );
  }

  return (
    <Screen testId="instruction">
      <div className="pt-8">
        <Title>{copy.instruction.heading}</Title>
      </div>

      <div className="flex min-h-48 w-full flex-1 items-center justify-center py-6">
        <VideoPlaceholder
          src={assets.instruction}
          label="Instruction clip"
          muted={false}
          autoPlay
          onEnded={onDone}
          className="aspect-[9/16] h-full max-h-full w-auto max-w-full rounded-2xl object-cover"
        />
      </div>

      <div className="w-full max-w-sm space-y-3 pb-4">
        <Button testId="instruction-cta" onClick={onDone}>
          {copy.instruction.cta}
        </Button>
      </div>
    </Screen>
  );
}
